import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MetaData from '../Layouts/MetaData';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const InventoryLog = () => {
    const [view, setView] = useState('products'); // 'products' | 'monthly'
    const [results, setResults] = useState([]);
    const [totalUnits, setTotalUnits] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [loading, setLoading] = useState(true);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [productOptions, setProductOptions] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productSearch, setProductSearch] = useState("");

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");

    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState("");

    // Load category (flattened) and brand options once
    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                const flat = [];
                (data.categories || []).forEach((c) => {
                    flat.push({ _id: c._id, name: c.name });
                    (c.children || []).forEach((child) => flat.push({ _id: child._id, name: `— ${child.name}` }));
                });
                setCategories(flat);
            } catch (error) { /* filters just stay empty */ }

            try {
                const { data } = await axios.get('/api/v1/brands');
                setBrands(data.brands || []);
            } catch (error) { /* filters just stay empty */ }
        };
        load();
    }, []);

    // Type-to-search for a specific product, debounced
    useEffect(() => {
        if (!productSearch) { setProductOptions([]); return; }
        const timeout = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/v1/products', { params: { keyword: productSearch } });
                setProductOptions(data.products || []);
            } catch (error) { /* ignore */ }
        }, 400);
        return () => clearTimeout(timeout);
    }, [productSearch]);

    // Guards against an older, slower request's response landing after a
    // newer one and overwriting it with mismatched (and crash-prone) data
    // — e.g. quickly switching between "By Product" and "Monthly Report".
    const requestIdRef = useRef(0);

    const fetchData = useCallback(async () => {
        const thisRequestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const params = { view };
            if (selectedProduct) params.productId = selectedProduct._id;
            if (selectedCategory) params.category = selectedCategory;
            if (selectedBrand) params.brand = selectedBrand;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;

            const { data } = await axios.get('/api/v1/admin/inventory-log', { params });

            if (thisRequestId !== requestIdRef.current) return; // a newer request has already started

            setResults(data.results);
            setTotalUnits(data.totalUnits);
            setTotalRevenue(data.totalRevenue);
        } catch (error) {
            console.error('Failed to load inventory log', error);
        } finally {
            if (thisRequestId === requestIdRef.current) setLoading(false);
        }
    }, [view, selectedProduct, selectedCategory, selectedBrand, fromDate, toDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const setToday = () => {
        const today = new Date().toISOString().slice(0, 10);
        setFromDate(today);
        setToDate(today);
    };

    const setThisMonth = () => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
        const today = now.toISOString().slice(0, 10);
        setFromDate(first);
        setToDate(today);
    };

    const resetFilters = () => {
        setSelectedProduct(null);
        setSelectedCategory("");
        setSelectedBrand("");
        setFromDate("");
        setToDate("");
    };

    const hasActiveFilters = selectedProduct || selectedCategory || selectedBrand || fromDate || toDate;

    return (
        <>
            <MetaData title="Admin: Inventory Log | 1313 Mart" />

            <h1 className="text-lg font-medium uppercase mb-4">Inventory Log</h1>

            <div className="bg-white rounded-xl shadow-lg p-5 flex flex-col gap-4">
                <div className="flex flex-wrap justify-between items-center gap-3">
                    <ToggleButtonGroup
                        size="small"
                        value={view}
                        exclusive
                        onChange={(e, val) => val && setView(val)}
                    >
                        <ToggleButton value="products">By Product</ToggleButton>
                        <ToggleButton value="monthly">Monthly Report</ToggleButton>
                    </ToggleButtonGroup>

                    <div className="flex flex-wrap items-center gap-2">
                        <button onClick={setToday} className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50">Today</button>
                        <button onClick={setThisMonth} className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50">This Month</button>
                        {hasActiveFilters && (
                            <button onClick={resetFilters} className="text-sm text-gray-500 px-2">Reset</button>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Autocomplete
                        size="small"
                        sx={{ width: 220 }}
                        options={productOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={selectedProduct}
                        onChange={(e, newValue) => setSelectedProduct(newValue)}
                        onInputChange={(e, newInput) => setProductSearch(newInput)}
                        renderInput={(params) => <TextField {...params} label="Specific product" placeholder="Search..." />}
                    />

                    <TextField select size="small" label="Category" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((c) => <MenuItem value={c._id} key={c._id}>{c.name}</MenuItem>)}
                    </TextField>

                    <TextField select size="small" label="Brand" value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All Brands</MenuItem>
                        {brands.map((b) => <MenuItem value={b} key={b}>{b}</MenuItem>)}
                    </TextField>

                    <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>

                <div className="bg-gray-50 rounded-lg px-4 py-3 flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                        {fromDate || toDate ? "Total in selected range" : "Total (all time)"}
                    </span>
                    <div className="flex gap-6">
                        <span className="text-sm text-gray-700">
                            Units Sold: <span className="font-semibold">{totalUnits.toLocaleString()}</span>
                        </span>
                        <span className="text-sm text-gray-700">
                            Revenue: <span className="font-semibold text-primary-green">HK${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </span>
                    </div>
                </div>

                {loading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                ) : results.length === 0 ? (
                    <p className="text-sm text-gray-400">No data for this selection.</p>
                ) : view === 'monthly' ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2 pr-2">Month</th>
                                    <th className="py-2 pr-2 text-right">Units Sold</th>
                                    <th className="py-2 text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.filter((r) => r._id && typeof r._id.month === 'number').map((r, i) => (
                                    <tr key={i} className="border-b last:border-0">
                                        <td className="py-2 pr-2">{MONTH_NAMES[r._id.month - 1]} {r._id.year}</td>
                                        <td className="py-2 pr-2 text-right">{r.unitsSold}</td>
                                        <td className="py-2 text-right font-medium">HK${r.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2 pr-2">Product</th>
                                    <th className="py-2 pr-2 text-right">Total Ordered</th>
                                    <th className="py-2 text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((p, i) => (
                                    <tr key={p._id || i} className="border-b last:border-0">
                                        <td className="py-2 pr-2 flex items-center gap-2">
                                            {p.image && <img src={p.image} alt={p.name} className="w-8 h-8 object-contain rounded" />}
                                            <span className="truncate max-w-xs">{p.name}</span>
                                        </td>
                                        <td className="py-2 pr-2 text-right">{p.unitsSold}</td>
                                        <td className="py-2 text-right font-medium">HK${p.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

export default InventoryLog;